/**
 * Turning model output into AI-suggested annotations.
 *
 * Shared by every source of suggestions — the open-vocabulary auto-detect
 * (useAutoLabel) and pre-labeling from the user's own trained model
 * (useModelPreLabel) — so both dedupe, clamp and resolve classes identically.
 */

/** Detections smaller than this in image pixels are noise, not objects. */
export const MIN_BOX_PIXELS = 4;
/** A suggestion overlapping an existing box by more than this is a duplicate. */
export const DUPLICATE_IOU = 0.5;

export const clamp01 = (value) => Math.min(1, Math.max(0, value));

/** IoU of two normalised {centerX, centerY, width, height} boxes. */
export const annIou = (a, b) => {
  const ax1 = a.centerX - a.width / 2, ay1 = a.centerY - a.height / 2;
  const ax2 = a.centerX + a.width / 2, ay2 = a.centerY + a.height / 2;
  const bx1 = b.centerX - b.width / 2, by1 = b.centerY - b.height / 2;
  const bx2 = b.centerX + b.width / 2, by2 = b.centerY + b.height / 2;
  const iw = Math.max(0, Math.min(ax2, bx2) - Math.max(ax1, bx1));
  const ih = Math.max(0, Math.min(ay2, by2) - Math.max(ay1, by1));
  const inter = iw * ih;
  const union = a.width * a.height + b.width * b.height - inter;
  return union > 0 ? inter / union : 0;
};

/**
 * Convert raw detections into annotations ready for the store.
 *
 * @param detections  [{ bbox:{x1,y1,x2,y2}, class, confidence, color }]
 * @param imageSize   { width, height } in original image pixels
 * @param existingAnns annotations already on the image (for duplicate rejection)
 * @param opts.classes         the project's current class list
 * @param opts.strict          drop detections with no matching class instead of creating one
 * @param opts.allowNewClasses false when re-filtering already-returned detections,
 *                             so a re-filter never re-creates classes
 * @param opts.probeToClassId  alias term (lowercased) -> class id
 *
 * Pure: it never touches the store. The caller commits `newClasses`.
 */
export const buildAiAnnotations = (detections, imageSize, existingAnns, opts = {}) => {
  const {
    classes = [],
    strict = false,
    allowNewClasses = true,
    probeToClassId = new Map(),
  } = opts;

  let classList = [...classes];
  const newClasses = [];
  let nextId = classList.reduce((max, c) => Math.max(max, Number(c.id) || 0), -1) + 1;

  const { width: W, height: H } = imageSize;
  const anns = [];
  let skipped = 0;

  (detections || []).forEach((det) => {
    const { x1, y1, x2, y2 } = det.bbox || {};
    if ([x1, y1, x2, y2].some((v) => typeof v !== 'number')) return;
    if (x2 - x1 < MIN_BOX_PIXELS || y2 - y1 < MIN_BOX_PIXELS) return;

    const ann = {
      centerX: clamp01((x1 + x2) / 2 / W),
      centerY: clamp01((y1 + y2) / 2 / H),
      width: clamp01((x2 - x1) / W),
      height: clamp01((y2 - y1) / H),
    };

    // Skip if it duplicates an existing box or an already-added suggestion
    const all = [...existingAnns, ...anns];
    if (all.some((existing) => annIou(ann, existing) > DUPLICATE_IOU)) return;

    // Resolve to a user class: first via the alias map (term -> classId), then
    // by a direct case-insensitive name match.
    const detName = String(det.class || 'object').trim();
    const key = detName.toLowerCase();
    let cls = null;
    const mappedId = probeToClassId.get(key);
    if (mappedId != null) cls = classList.find((c) => String(c.id) === String(mappedId));
    if (!cls) cls = classList.find((c) => (c.name || '').trim().toLowerCase() === key);

    if (!cls) {
      if (strict || !allowNewClasses) { skipped += 1; return; }
      cls = {
        id: String(nextId++),
        name: detName,
        color: det.color || '#FF6384',
        originalColor: det.color || '#FF6384',
      };
      classList = [...classList, cls];
      newClasses.push(cls);
    }

    anns.push({
      ...ann,
      classId: String(cls.id),
      class: cls,
      isAI: true,
      confidence: det.confidence,
    });
  });

  return { anns, newClasses, skipped };
};

/** True when an image carries no annotation of any kind yet. */
export const isUnlabeled = (img) =>
  (img?.annotations?.length ?? 0) === 0 && (img?.polygons?.length ?? 0) === 0;

/** Resolve an image entry to a Blob we can send to a model. */
export const getImageBlob = async (img) => {
  if (img.file) return img.file;
  const src = img.dataUrl || img.url_display || img.url;
  if (!src) throw new Error('No image source available');
  const res = await fetch(src, { mode: 'cors' });
  if (!res.ok) throw new Error(`Failed to fetch image (${res.status})`);
  return res.blob();
};

/** True for "the request was aborted", which is a stop — not a failure. */
export const isAbortError = (err) =>
  err?.name === 'CanceledError' ||
  err?.name === 'AbortError' ||
  err?.code === 'ERR_CANCELED' ||
  err?.message === 'canceled';
